import type { AxiosInstance, AxiosRequestConfig } from 'axios'

// @ts-expect-error: api-client is imported
import AxiosService from '@deltax/api-client'

import appConfig from '@/constants/appConfig'
import AppService from '@/services/appService'

// Errors
import errorCodes from '@/constants/errorCodes'
import UserAccountExpiredException from '@/errors/UserAccountExpiredException'
import InvalidBusinessProfileRequestedError from '@/errors/InvalidBusinessProfileRequestedError'
import InvalidRequestDataException from '@/errors/InvalidRequestDataException'
import ForbiddenBusinessProfileRequestedError from '@/errors/ForbiddenBusinessProfileRequestedError'
import UnauthorizedError from '@/errors/UnauthorizedError'
import TokenMalformedException from '@/errors/TokenMalformedError'
import TokenExpiredException from '@/errors/TokenExpiredException'
import TokenInvalidException from '@/errors/TokenInvalidException'
import RefreshTokenExpiredException from '@/errors/RefreshTokenExpiredException'
import Bugsnag from '@bugsnag/js'
import { inject } from 'vue'
import { useI18n } from 'vue-i18n'

class ApiClient {
  private readonly baseUrl: string

  private readonly axiosInstance: AxiosInstance

  readonly apiMethods: any

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
    const axiosService = new AxiosService(
      this.baseUrl,
      appConfig.STUNNR_BE_CLIENT_ID,
      import.meta.env.DEV ? 'be.stunnr.co' : window.location.hostname,
      this.handleError,
    )
    this.axiosInstance = axiosService.axiosInstance
    this.apiMethods = axiosService.getApiMethods()
  }

  public async get<T>(url: string, params?: any, config?: AxiosRequestConfig): Promise<T> {
    const requestConfg: AxiosRequestConfig = { params }
    config ? Object.assign(requestConfg, config) : null
    const response = await this.axiosInstance.get<T>(url, requestConfg)
    return response.data
  }

  public async post<T>(url: string, data: any): Promise<T> {
    const response = await this.axiosInstance.post<T>(this.baseUrl + url, data)
    return response.data
  }

  public async patch<T>(url: string, data?: any): Promise<T> {
    const response = await this.axiosInstance.patch<T>(this.baseUrl + url, data)
    return response.data
  }

  private notifyError = function (error: any) {
    const notify = inject('notify') as Function
    const { t } = useI18n()

    if (error && error.response && error.response.data && error.response.data.message) {
      Bugsnag.notify(new Error(error.response.data.message))
    } else {
      Bugsnag.notify(new Error(error))
    }
    notify(t('errors.somethingWentWrong'), 'error')
  }

  private handleError(error: any) {
    const appService = new AppService()

    if (
      error &&
      error.response &&
      (error.response.data || error.response.status === errorCodes.Unauthorized)
    ) {
      const errorCode: number =
        error.response.data.errorCode === undefined
          ? error.response.status
          : error.response.data.errorCode
      switch (errorCode) {
        case errorCodes.UserAccountExpiredException:
          return Promise.reject(new UserAccountExpiredException(error))
        case errorCodes.InvalidBusinessProfileRequested:
          return Promise.reject(new InvalidBusinessProfileRequestedError(error))
        case errorCodes.InvalidRequestDataException:
          return Promise.reject(new InvalidRequestDataException(error.response.data?.message))
        case errorCodes.ForbiddenBusinessProfileRequested:
          return Promise.reject(new ForbiddenBusinessProfileRequestedError(error))
        case errorCodes.TokenMalformedException:
          appService.clearAuthData(false)
          return Promise.reject(new TokenMalformedException(error.response.data?.message))
        case errorCodes.TokenExpiredException:
          return Promise.reject(new TokenExpiredException(error.response.data?.message))
        case errorCodes.TokenInvalidException:
          appService.clearAuthData(false)
          return Promise.reject(new TokenInvalidException(error.response.data?.message))
        case errorCodes.Unauthorized:
          appService.clearAuthData()
          return Promise.reject(new UnauthorizedError(error.response.data?.message))
        case errorCodes.RefreshTokenExpired:
          appService.clearAuthData()
          return Promise.reject(new RefreshTokenExpiredException(error))
        default:
          this.notifyError(error)
          return Promise.reject(error)
      }
    } else if (error) {
      this.notifyError(error)
      return Promise.reject(error)
    }
  }
}

export default ApiClient
